import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Slate-gradient hero header used across the app's section pages (mirrors the developer
 * Projects page). Title + subtitle on the left, action buttons on the right, and an optional
 * `children` row below the band for filters/search so the page reads as one cohesive surface.
 *
 * Props:
 *  - title:      string (required)
 *  - subtitle:   string
 *  - icon:       lucide icon component
 *  - actions:    node rendered on the right (buttons, etc.)
 *  - backTo:     route string; renders a "Back" link above the title
 *  - backLabel:  label for the back link (default "Back")
 *  - children:   node rendered in a row beneath the title band (e.g. a filter bar)
 */
const PageHeader = ({ title, subtitle, icon: Icon, actions, backTo, backLabel = "Back", children }) => (
  <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 to-slate-600 p-5 lg:p-6">
    {backTo && (
      <Link
        to={backTo}
        className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-slate-300 hover:text-white transition-colors"
      >
        <ArrowLeft size={14} /> {backLabel}
      </Link>
    )}
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3 min-w-0">
        {Icon && (
          <div className="hidden sm:flex w-10 h-10 shrink-0 rounded-xl bg-white/10 text-white items-center justify-center">
            <Icon size={20} />
          </div>
        )}
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-semibold text-white tracking-tight truncate">{title}</h1>
          {subtitle && <p className="text-xs text-slate-300 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-col sm:flex-row gap-3 sm:items-center shrink-0">{actions}</div>}
    </div>
    {children && <div className="mt-4">{children}</div>}
  </div>
);

export default PageHeader;
